import React from 'react';
import SimpleAdmin, { DefaultOptions } from 'components/simpleAdmin';
import { useDispatch, useSelector } from 'react-redux';
import { apiDelete, apiGet, apiPatch, apiPost } from 'services';
import { ArrayHelpers, Help, SystemText } from '@salesperformanceint/e4shared';
import { adminLoading, handleError } from 'actions/admin';
import { setShowSuccess } from 'actions/ui';
import queryString from 'query-string';
import SortPositiveIndicators from './components/sortPositiveIndicators';

export const AdminPositiveIndicators = ({ history }) => {
    const dispatch = useDispatch();
    const queryStringValues = queryString.parse(location.search);
    const cid = parseInt(queryStringValues.cid);

    const [showSort, setShowSort] = React.useState(false);
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [range, setRange] = React.useState(5);
    const [competencies, setCompetencies] = React.useState([]);
    const [allRatingGroups, setAllRatingGroups] = React.useState([]);
    const [ratingGroups, setRatingGroups] = React.useState([]);
    const [goals, setGoals] = React.useState([]);

    const defaults = {
        ratingTypeRef: 'Text',
        weighting: 1,
        active: true,
    };

    const [defaultValues, setDefaultValues] = React.useState({ ...defaults });

    React.useEffect(() => {
        const temp = { ...defaults };

        if (ArrayHelpers.existsAndHasLength(ratingGroups)) {
            temp.ratingGroupId = ratingGroups.find(x => x.ratingGroupType === 0)?.id || 0;
        }

        if (queryStringValues.cid) {
            temp.competency_Id = parseInt(queryStringValues.cid, 10);
        }

        setDefaultValues(temp);
    }, [queryStringValues.cid, ratingGroups]);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        setRange(user?.companySettings?.display?.ratingRange);
    }, [user?.companySettings?.display?.ratingRange]);

    if (!user.companySettings.development.usePositiveIndicators) {
        history.replace('/error');
    }

    const translations = {
        title: SystemText('Admin/PositiveIndicators/Title'),
        titlePlural: `${SystemText('Admin/PositiveIndicators/Title/Plural')}${
            queryStringValues.cname ? ` - ${queryStringValues.cname}` : ''
        }`,
        ratingGroupTypeText: SystemText('Admin/PositiveIndicators/RatingGroupType/Text'),
        ratingGroupTypeMetric: SystemText('Admin/PositiveIndicators/RatingGroupType/Metric'),
        columnsSeq: SystemText('Admin/PositiveIndicators/Table/Columns/Seq'),
        columnsName: SystemText('Admin/PositiveIndicators/Table/Columns/Name'),
        columnsCompetency: SystemText('Admin/PositiveIndicators/Table/Columns/Competency'),
        columnsType: SystemText('Admin/PositiveIndicators/Table/Columns/Type'),
        columnsGroup: SystemText('Admin/PositiveIndicators/Table/Columns/Group'),
        success: SystemText('Admin/PositiveIndicators/Success'),
        formsName: SystemText('Admin/PositiveIndicators/Forms/Name'),
        formsHelpName: SystemText('Admin/PositiveIndicators/Forms/Help/Name'),
        formsSysName: SystemText('Admin/PositiveIndicators/Forms/SysName'),
        formsHelpSysName: SystemText('Admin/PositiveIndicators/Forms/Help/SysName'),
        formsCompetency: SystemText('Admin/PositiveIndicators/Forms/Competency'),
        formsHelpCompetency: SystemText('Admin/PositiveIndicators/Forms/Help/Competency'),
        formsType: SystemText('Admin/PositiveIndicators/Forms/Type'),
        formsHelpType: SystemText('Admin/PositiveIndicators/Forms/Help/Type'),
        formsRatingGroup: SystemText('Admin/PositiveIndicators/Forms/RatingGroup'),
        formsHelpRatingGroup: SystemText('Admin/PositiveIndicators/Forms/Help/RatingGroup'),
        formsGoal: SystemText('Admin/PositiveIndicators/Forms/Goal'),
        formsHelpGoal: SystemText('Admin/PositiveIndicators/Forms/Help/Goal'),
        formsWeighting: SystemText('Admin/PositiveIndicators/Forms/Weighting'),
        formsHelpWeighting: SystemText('Admin/PositiveIndicators/Forms/Help/Weighting'),
        formsErrorWeighting: SystemText('Admin/PositiveIndicators/Forms/Error/Weighting'),
        formsActive: SystemText('Admin/PositiveIndicators/Forms/Active'),
        formsHelpActive: SystemText('Admin/PositiveIndicators/Forms/Help/Active'),
        tableActive: SystemText('Admin/PositiveIndicators/Table/Active'),
        tableInActive: SystemText('Admin/PositiveIndicators/Table/InActive'),
    };

    const filterGroups = value => {
        if (allRatingGroups) {
            const filteredGroups = allRatingGroups.filter(
                x => (x.ratingGroupType === 0 && value === 'Text') || (x.ratingGroupType === 1 && value === 'Metrics')
            );

            setRatingGroups(filteredGroups);
        }
    };

    const ratingGroupTypes = [
        { id: 0, ref: 'Text', name: translations.ratingGroupTypeText },
        { id: 1, ref: 'Metrics', name: translations.ratingGroupTypeMetric },
    ];

    const api = {
        load: () => {
            setLoading(true);
            apiGet(
                `positive-indicators/${queryStringValues.cid ?? ''}`,
                user.guid,
                response => {
                    const myData = response.map(pi => ({
                        ...pi,
                        competency_Id: pi.competency_Id,
                        competencyName: pi.competency?.name,
                        ratingGroupId: pi.ratingGroup?.id ?? pi.ratingGroupId,
                        ratingGroupTypeName: ratingGroupTypes.find(t => t.id === pi.ratingType).name,
                        ratingTypeRef: ratingGroupTypes.find(t => t.id === pi.ratingType).ref,
                        useMetrics: pi.ratingType === ratingGroupTypes.find(t => t.ref === 'Metrics').id,
                        cannotDelete: !user.support && !!pi.system_Name,
                    }));

                    setData(myData);
                    setLoading(false);
                },
                error => {
                    console.error(error);
                    handleError(dispatch, error);
                }
            );
        },
        save: (pi, callback) => {
            pi.competency = { id: pi.competency_Id };
            pi.ratingGroup = { id: pi.ratingGroupId };
            apiPost('positive-indicators', pi, user.guid, () => {
                dispatch(setShowSuccess(translations.success));
                callback?.();
            });
        },
        update: (pi, callback, bulk) => {
            pi.competency = { id: pi.competency_Id };
            pi.ratingGroup = { id: pi.ratingGroupId };
            apiPatch('positive-indicators', pi, user.guid, () => {
                !bulk && dispatch(setShowSuccess(translations.success));
                callback?.();
            });
        },
        delete: (ids, callback) => {
            if (!Array.isArray(ids)) ids = [ids];
            ids.forEach((id, idx) => {
                apiDelete(`positive-indicators/${id}`, null, user.guid, () => {
                    if (idx + 1 === ids.length) {
                        api.load();
                        callback?.();
                    }
                });
            });
        },
    };

    const options = {
        ...DefaultOptions(),
        cannotDeleteMessage: SystemText('Admin/Table/Delete/System', translations.title.toLowerCase()),
        mainColumnIndex: queryStringValues.cid ? 1 : 0,
        sortBy: queryStringValues.cid ? 'sequence' : 'statement',
        label: translations.title,
        showDelete: true,
        showActive: true,
        labelPlural: translations.titlePlural,
    };

    let tableColumns = [];

    const piArray = range === 4 ? [1, 2, 3, 4] : [1, 2, 3, 4, 5];

    if (queryStringValues.cid) {
        tableColumns = tableColumns.concat([
            {
                label: translations.columnsSeq,
                dataProperty: 'sequence',
                isNumeric: true,
                format: (val, col) => val + 1,
            },
        ]);
    }

    tableColumns = tableColumns.concat([
        {
            label: translations.columnsName,
            dataProperty: 'statement',
        },
        {
            label: translations.columnsCompetency,
            dataProperty: 'competencyName',
            hide: queryStringValues.cid && competencies[0],
        },
        {
            label: translations.columnsGroup,
            dataProperty: 'rating_Group_Name',
            renderer: d => {
                const group = allRatingGroups.find(g => g.id === d.rating_Group_Id);
                return (
                    <span>
                        {d.rating_Group_Name}
                        {group && (
                            <Help id={`rating-group-help-${d.id}`}>
                                {piArray.map((i, idx) => (
                                    <div key={`rating-group-${group.id}-${idx}-${range}`} className="flex flex-row">
                                        <div style={{ minWidth: '15px' }}>{i}:</div>
                                        <div className="flex-grow">{group[`option${i}`]}</div>
                                    </div>
                                ))}
                            </Help>
                        )}
                    </span>
                );
            },
        },
        {
            label: translations.columnsType,
            dataProperty: 'ratingGroupTypeName',
        },
    ]);

    let formFields = [
        {
            label: translations.formsSysName,
            type: 'text',
            readonly: !user.support,
            mandatory: true,
            dataProperty: 'system_Name',
            maxLength: 50,
            hideField: (values, entity) => !user.support && !values.system_Name,
            help: translations.formsHelpSysName,
        },
        {
            label: translations.formsName,
            type: 'text',
            dataProperty: 'statement',
            mandatory: true,
            maxLength: 300,
            generalErrors: true,
            help: translations.formsHelpName,
        },
        {
            label: translations.formsCompetency,
            type: 'select',
            dataProperty: 'competency_Id',
            data: queryStringValues.cid
                ? [{ id: parseInt(queryStringValues.cid, 10), name: queryStringValues.cname }]
                : competencies.map(c => ({ id: c.id, name: c.full_Name })).sort((a, b) => (a > b ? 1 : -1)),
            mandatory: true,
            help: translations.formsHelpCompetency,
            readonly: queryStringValues.cid,
        },
        {
            label: translations.formsType,
            type: 'buttons',
            dataProperty: 'ratingTypeRef',
            buttons: [
                {
                    label: translations.ratingGroupTypeText,
                    value: 'Text',
                },
                {
                    label: translations.ratingGroupTypeMetric,
                    value: 'Metrics',
                },
            ],
            help: translations.formsHelpType,
            onChange: filterGroups,
        },
        {
            label: translations.formsRatingGroup,
            type: 'select',
            dataProperty: 'ratingGroupId',
            data: ratingGroups,
            mandatory: true,
            help: translations.formsHelpRatingGroup,
        },
        {
            label: translations.formsGoal,
            type: 'select',
            dataProperty: 'goal',
            data: goals,
            toggleProperty: 'ratingTypeRef',
            toggleValue: 'Metrics',
            mandatory: true,
            help: translations.formsHelpGoal,
        },
        {
            label: translations.formsWeighting,
            type: 'number',
            dataProperty: 'weighting',
            mandatory: true,
            help: translations.formsHelpWeighting,
            validation: x => {
                const n = x.weighting;
                return n <= 0 || n % 1 !== 0 ? translations.formsErrorWeighting : '';
            },
        },
        {
            label: translations.formsActive,
            help: translations.formsHelpActive,
            type: 'toggle',
            dataProperty: 'active',
            inTitleBar: true,
        },
    ];

    React.useEffect(() => {
        api.load();

        apiGet(`competency/${queryStringValues.cid ?? ''}`, user.guid, resp => {
            setCompetencies(Array.isArray(resp) ? resp : [resp]);
        });

        apiGet('competency-rating-groups', user.guid, response => {
            setAllRatingGroups(response);
            setRatingGroups(response.filter(x => x.ratingGroupType === 0));
        });
        apiGet('all-quotas', user.guid, setGoals);
    }, []);

    const extraButtons = queryStringValues.cid ? (
        <button
            className="btn btn-primary"
            onClick={() => {
                setShowSort(true);
            }}
        >
            <i className="fa fa-sort-amount-asc mr10" />
            {SystemText('Admin/PositiveIndicators/Buttons/Sort')}
        </button>
    ) : null;

    return (
        <div>
            <SimpleAdmin
                data={data}
                fields={formFields}
                options={options}
                columns={tableColumns}
                remove={api.delete}
                add={api.save}
                load={api.load}
                loading={loading}
                update={api.update}
                onCancel={() => filterGroups(false)}
                defaultValues={defaultValues}
                extraButtons={extraButtons}
                filterTextOn={SystemText('Admin/PositiveIndicators/FilterOn')}
                filterColumn="active"
                onUpdateLoad={entity => {
                    if (entity) {
                        filterGroups(ratingGroupTypes.find(r => r.id === entity.ratingGroup.ratingGroupType)?.ref);
                    }
                }}
            />
            {ArrayHelpers.existsAndHasLength(data) && (
                <SortPositiveIndicators
                    show={showSort}
                    close={() => {
                        setShowSort(false);
                    }}
                    refresh={() => {
                        dispatch(setShowSuccess(SystemText('Admin/PositiveIndicators/OrderSuccess')));
                        api.load();
                    }}
                    data={data.map(x => ({ ...x, name: x.statement }))}
                />
            )}
        </div>
    );
};

export default AdminPositiveIndicators;
